import {
  Box,
  Button,
  Heading,
  HStack,
  VStack,
  Text,
  Badge,
  Table,
  IconButton,
  Tooltip,
  Toaster,
  createToaster,
} from '@chakra-ui/react'
import { Plus, Trash2, Mail, RefreshCw, Clock } from 'lucide-react'
import { useState } from 'react'
import { useAgency } from '@/features/agencies/hooks/useAgency'
import { useUsers, useDeleteUser, useResendInvitation } from '../hooks/useUsers'
import { UserInviteModal } from '../components/UserInviteModal'
import { useSubscription } from '@/shared/hooks/useSubscription'
import { UsageGauge } from '@/shared/components/UsageGauge'

const toaster = createToaster({
  placement: 'top-end',
  duration: 4000,
})

export function UserManagementPage() {
  const { data: agency } = useAgency()
  const { data: users, isLoading } = useUsers(agency?.id)
  const deleteUser = useDeleteUser()
  const resendInvitation = useResendInvitation()
  const [showInviteModal, setShowInviteModal] = useState(false)
  const { usage, limits, canAddUser } = useSubscription()

  const handleDeleteUser = async (userId: string) => {
    if (!agency?.id) return

    if (confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) {
      try {
        await deleteUser.mutateAsync({ userId, agencyId: agency.id })
        toaster.create({
          title: 'Utilisateur supprimé',
          description: 'L\'utilisateur a été supprimé avec succès',
          type: 'success',
        })
      } catch (error) {
        console.error('Error deleting user:', error)
        const errorMessage = error instanceof Error ? error.message : 'Erreur lors de la suppression'
        toaster.create({
          title: 'Erreur',
          description: errorMessage,
          type: 'error',
        })
      }
    }
  }

  const handleResendInvitation = async (userId: string, email: string) => {
    if (!agency?.id) return

    try {
      await resendInvitation.mutateAsync({ userId, email, agencyId: agency.id })
      toaster.create({
        title: 'Invitation renvoyée',
        description: `Un nouvel email d'invitation a été envoyé à ${email}`,
        type: 'success',
      })
    } catch (error) {
      console.error('Error resending invitation:', error)
      const errorMessage = error instanceof Error ? error.message : 'Erreur lors du renvoi de l\'invitation'
      toaster.create({
        title: 'Erreur',
        description: errorMessage,
        type: 'error',
      })
    }
  }

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return (
          <Badge colorPalette="red" size="sm">
            Administrateur
          </Badge>
        )
      case 'MANAGER':
        return (
          <Badge colorPalette="blue" size="sm">
            Gestionnaire
          </Badge>
        )
      case 'ACCOUNTANT':
        return (
          <Badge colorPalette="purple" size="sm">
            Comptable
          </Badge>
        )
      case 'USER':
        return (
          <Badge colorPalette="gray" size="sm">
            Utilisateur
          </Badge>
        )
      default:
        return (
          <Badge colorPalette="gray" size="sm">
            {role}
          </Badge>
        )
    }
  }

  const getInvitationStatusBadge = (status?: string) => {
    switch (status) {
      case 'pending':
        return (
          <Badge colorPalette="orange" size="sm">
            <Clock size={12} />
            En attente
          </Badge>
        )
      case 'expired':
        return (
          <Badge colorPalette="red" size="sm">
            Expirée
          </Badge>
        )
      case 'active':
      default:
        return (
          <Badge colorPalette="green" size="sm">
            Actif
          </Badge>
        )
    }
  }

  return (
    <Box>
      <VStack align="stretch" gap={6}>
        {/* Header */}
        <HStack justify="space-between">
          <Box>
            <Heading size="lg" mb={2}>
              Gestion des utilisateurs
            </Heading>
            <Text color="gray.600">
              Invitez et gérez les utilisateurs de votre agence
            </Text>
          </Box>
          <Tooltip.Root disabled={canAddUser()}>
            <Tooltip.Trigger asChild>
              <Button
                colorPalette="brand"
                onClick={() => setShowInviteModal(true)}
                disabled={!canAddUser()}
              >
                <Plus size={20} />
                Inviter un utilisateur
              </Button>
            </Tooltip.Trigger>
            <Tooltip.Positioner>
              <Tooltip.Content>
                Limite d'utilisateurs atteinte. Passez à une formule supérieure pour ajouter plus d'utilisateurs.
              </Tooltip.Content>
            </Tooltip.Positioner>
          </Tooltip.Root>
        </HStack>

        {/* Usage Gauge */}
        {usage && limits && (
          <Box bg="white" p={6} borderRadius="lg" borderWidth="1px">
            <UsageGauge
              label="Utilisateurs"
              current={usage.users}
              limit={limits.users}
            />
          </Box>
        )}

        {/* Users Table */}
        <Box bg="white" borderRadius="lg" borderWidth="1px" overflow="hidden">
          {isLoading ? (
            <Box p={8} textAlign="center">
              <Text color="gray.600">Chargement...</Text>
            </Box>
          ) : !users || users.length === 0 ? (
            <Box p={8} textAlign="center">
              <Mail size={48} style={{ margin: '0 auto 16px', opacity: 0.3 }} />
              <Text color="gray.600" mb={4}>
                Aucun utilisateur pour le moment
              </Text>
              <Tooltip.Root disabled={canAddUser()}>
                <Tooltip.Trigger asChild>
                  <Button
                    colorPalette="brand"
                    onClick={() => setShowInviteModal(true)}
                    disabled={!canAddUser()}
                  >
                    <Plus size={16} />
                    Inviter le premier utilisateur
                  </Button>
                </Tooltip.Trigger>
                <Tooltip.Positioner>
                  <Tooltip.Content>
                    Limite d'utilisateurs atteinte. Passez à une formule supérieure pour ajouter plus d'utilisateurs.
                  </Tooltip.Content>
                </Tooltip.Positioner>
              </Tooltip.Root>
            </Box>
          ) : (
            <Table.Root>
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>Nom</Table.ColumnHeader>
                  <Table.ColumnHeader>Email</Table.ColumnHeader>
                  <Table.ColumnHeader>Rôle</Table.ColumnHeader>
                  <Table.ColumnHeader>Statut</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="right">Actions</Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {users.map((user) => (
                  <Table.Row key={user.id}>
                    <Table.Cell>
                      <Text fontWeight="medium">
                        {user.first_name} {user.last_name}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text color="gray.600">{user.email}</Text>
                    </Table.Cell>
                    <Table.Cell>
                      {/* Note: Le rôle devrait venir de user_residences, à implémenter */}
                      {getRoleBadge('USER')}
                    </Table.Cell>
                    <Table.Cell>
                      {getInvitationStatusBadge(user.invitation_status)}
                    </Table.Cell>
                    <Table.Cell textAlign="right">
                      <HStack gap={2} justify="flex-end">
                        {user.invitation_status === 'pending' && (
                          <Tooltip.Root>
                            <Tooltip.Trigger asChild>
                              <IconButton
                                variant="ghost"
                                colorPalette="blue"
                                aria-label="Renvoyer l'invitation"
                                onClick={() => handleResendInvitation(user.id, user.email)}
                                loading={resendInvitation.isPending}
                              >
                                <RefreshCw size={16} />
                              </IconButton>
                            </Tooltip.Trigger>
                            <Tooltip.Positioner>
                              <Tooltip.Content>Renvoyer l'invitation</Tooltip.Content>
                            </Tooltip.Positioner>
                          </Tooltip.Root>
                        )}
                        <IconButton
                          variant="ghost"
                          colorPalette="red"
                          aria-label="Supprimer"
                          onClick={() => handleDeleteUser(user.id)}
                          loading={deleteUser.isPending}
                        >
                          <Trash2 size={16} />
                        </IconButton>
                      </HStack>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          )}
        </Box>
      </VStack>

      {/* Invite Modal */}
      <UserInviteModal isOpen={showInviteModal} onClose={() => setShowInviteModal(false)} />

      {/* Toaster */}
      <Toaster toaster={toaster}>
        {(toast) => (
          <Box
            bg={toast.type === 'success' ? 'green.500' : toast.type === 'error' ? 'red.500' : 'blue.500'}
            color="white"
            p={4}
            borderRadius="md"
            boxShadow="lg"
          >
            <VStack align="start" gap={1}>
              {toast.title && <Text fontWeight="bold">{toast.title}</Text>}
              {toast.description && <Text fontSize="sm">{toast.description}</Text>}
            </VStack>
          </Box>
        )}
      </Toaster>
    </Box>
  )
}
